import React, { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../../../AuthContextWrapper";
import { FinancialConvoContext } from "FinancialConvoContextWrapper";
import { FinancialConvo } from "models/FinancialConvo";
import { Box, Button, Typography, Link } from "@mui/material";

import advisor1 from "../../assets/financial-advisor-man-1x.png";
import advisor15 from "../../assets/financial-advisor-man-1.5x.png";
import advisor2 from "../../assets/financial-advisor-man-2x.png";
import advisor3 from "../../assets/financial-advisor-man-3x.png";

import { FlowScreenWrapper } from "../shared/FlowScreenWrapper";

export enum Outcomes {
  complete = "Complete",
  alreadySubmitted = "Already Submitted",
}

export const GetStartedScreen = (): JSX.Element => {
  const navigate = useNavigate();
  const { flow, activeEstate, user } = useAuthContext()
  const { latestFinancialConvo, setLatestFinancialConvo, allFinancialConvos } =
    useContext(FinancialConvoContext);
  const { screenName } = useParams();
  const currentScreen = flow?.getCurrentFlowScreen(screenName);

  const handleComplete = async (): Promise<boolean> => {
    if (!currentScreen) throw Error("couldn't determine currentScreen");

    if (!activeEstate?.id || !user?.id) {
      throw "No estate or user ID";
    }

    let outcome;

    if (
      (allFinancialConvos && allFinancialConvos.length > 1) ||
      (latestFinancialConvo && latestFinancialConvo.hasCompletedRequest)
    ) {
      // If more than 1 financial convo for the user/estate,
      // or if the current financial convo has been completed,
      // go to Already Completed screen
      outcome = Outcomes.alreadySubmitted;
    } else {
      outcome = Outcomes.complete;
      const financialConvo = latestFinancialConvo || new FinancialConvo();

      try {
        await financialConvo.saveRequest(activeEstate?.id, user?.id);
        setLatestFinancialConvo && setLatestFinancialConvo(financialConvo);
      } catch (error) {
        throw new Error("could not save data");
      }
    }

    flow?.navigateToNext(currentScreen, outcome, navigate);
    return true;
  };

  return currentScreen ? (
    <FlowScreenWrapper hideSectionHeading={true} centerContent={true}>
      <>
        <Box
          sx={{
            my: 2,
            textAlign: "center",
          }}
        >
          <img
            srcSet={`${advisor15} 1.5x, ${advisor2} 2x, ${advisor3} 3x`}
            src={advisor1}
            alt="Financial Advisor wearing glasses with his left hand on his right, ready to help."
            title="Financial Advisor Ready to Help"
          />
        </Box>
        <Box
          sx={{
            my: 2,
            textAlign: "center",
          }}
        >
          <Typography component="h2" variant="h3">
            We’re eager to help—first, let’s get started with a few simple
            questions
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            my: "1.5rem",
          }}
        >
          <Button
            variant="contained"
            onClick={handleComplete}
            sx={{
              mb: "1rem",
            }}
          >
            Get Started
          </Button>
          <Typography
            variant="body2"
            sx={{
              mx: "auto",
              color: "rgba(0,0,0,0.6)",
              fontSize: "0.75rem",
              fontWeight: "500",
              lineHeight: "1rem",
              width: "100%",
              textAlign: "center",
            }}
          >
            By clicking “Get started,” you agree to our{" "}
            <Link href="https://www.weareatticus.com/privacy-policy">
              Privacy Policy
            </Link>{" "}
            and to share the estate’s information for the purposes of connecting
            you to a Financial Advisor.
          </Typography>
        </Box>
      </>
    </FlowScreenWrapper>
  ) : (
    <></>
  );
};
